import React, { useEffect, useState } from 'react'

import { useRoute } from '@react-navigation/native'
import { FlashList } from '@shopify/flash-list'
import { publishEvent } from '@zet/analytics'
import { useDebounce } from '@zet/common/hooks/useDebounce'
import {
  useCustomerCount,
  useListOfCustomer,
} from '@zet/customer/src/api/hooks'
import { Stack } from '@zet/design-system'
import { useUserContext } from '@zet/feature-user'

import CustomerCard from '../../components/CustomerCard'
import CustomerListSkeleton from '../../layout/customerListSkeleton'
import useMyCustomersStore from '../../store/myCustomers'
import EmptyCustomers from './EmptyCustomers'

export default function VerifiedCustomersList({ navigation }) {
  const [isRefresh, setIsRefresh] = useState<boolean>(false)
  const route = useRoute()
  const searchedText = useMyCustomersStore((state) => state.searchText)
  const { data: subAgent } = useUserContext()
  const debouncedSearchText = useDebounce(searchedText?.trim() as never, 600)
  const sortType = useMyCustomersStore((state) => state.sortType)
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetching,
    isFetchingNextPage,
  } = useListOfCustomer({
    searchText: debouncedSearchText,
    sortType: sortType,
    selectedStatus: [],
    subAgentId: subAgent?.id || '',
    type: 'verified',
  })

  const { refetch: customerCountRefetch } = useCustomerCount(
    route?.params?.subAgentId || ''
  )

  const setSortingOptions = useMyCustomersStore(
    (state) => state.setSortingOptions
  )

  useEffect(() => {
    setSortingOptions('verified')
    const unsubscribe = navigation.addListener('focus', () => {
      setSortingOptions('verified')
    })
    return unsubscribe
  }, [navigation])

  useEffect(() => {
    const unsubscribe = navigation.addListener('tabPress', (e) => {
      publishEvent('click_action', {
        item_name: 'add_customer_tab_navbar',
        cta_name: 'verified',
      })
    })

    return unsubscribe
  }, [navigation])

  const renderItem = ({ item, index }) => {
    return (
      <CustomerCard
        name={item.fullName}
        phone={item.phoneNumber}
        subAgentName={item.subAgentName}
        eligible={item.eligibleCategoriesAsList}
        preapprovedBrands={item.preapprovedBrands}
        creditScoreLevel={item.creditScoreLevel}
        isVerified={true}
        customerId={item.id}
        isBureauReportExpired={item.bureauReportExpired}
      />
    )
  }

  const customersList =
    data?.pages
      ?.map((page) => page?.customerList)
      .flat()
      .filter((item) => Boolean(item)) || []

  const onRefresh = () => {
    setIsRefresh(true)
    customerCountRefetch()
    refetch().finally(() => setIsRefresh(false))
  }

  if (isLoading) {
    return (
      <>
        {Array(7)
          .fill({})
          .map((_, index) => (
            <React.Fragment key={index}>
              <CustomerListSkeleton />
            </React.Fragment>
          ))}
      </>
    )
  }

  return (
    <FlashList
      data={customersList}
      renderItem={renderItem}
      keyExtractor={(_, index) => index?.toString()}
      ListEmptyComponent={<EmptyCustomers />}
      keyboardDismissMode="on-drag"
      keyboardShouldPersistTaps="always"
      estimatedItemSize={70}
      ListFooterComponent={
        isFetchingNextPage ? (
          <>
            {Array(2)
              .fill({})
              .map((_, index) => (
                <Stack key={index} className="bg-white py-2">
                  <CustomerListSkeleton />
                </Stack>
              ))}
          </>
        ) : (
          <Stack className="h-1" />
        )
      }
      onEndReached={() => {
        if (!isFetching && hasNextPage) {
          fetchNextPage()
        }
      }}
      onRefresh={onRefresh}
      refreshing={isRefresh}
    />
  )
}
